import React, {
  ReactElement,
  useRef,
  useState,
  ReactNode,
  useEffect,
  WheelEvent,
  useCallback,
  useMemo,
} from "react";

import classNames from "classnames";
import isEqual from "lodash.isequal";
import { TabHeader } from "./components/tab-header";
import TabContent from "./components/tab-content";
import {
  useDetermineMinMax,
  useDetermineScrollability,
  useEventListener,
  useThrottledResizeObserver,
  useUpdateBreakPoint,
} from "./tabs.utils";
import { get } from "../utils";
import { TabsProps } from "./tabs.types";

export const Tabs = ({
  defaultTab,
  activeTab: controlledActiveTab,
  breakpoints,
  className,
  onTabClick,
  children,
  forceRender = false,
  rtl,
  scrollLeftArrowLabel = "Scroll Tabs to the Left",
  scrollRightArrowLabel = "Scroll Tabs to the Right",
  "aria-label": ariaLabel,
  navAriaLabel,
  "aria-describedby": ariaDescribedby,
  _privateProps,
  ...rest
}: TabsProps): ReactElement => {
  // State
  const [x, setX] = useState<number>(0);
  const [uncontrolledActiveTab, setUnControlledActiveTab] = useState<number>(
    defaultTab || 0
  );
  const renderTabs = controlledActiveTab || uncontrolledActiveTab;
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [lastX, setLastX] = useState<number>(0);
  const [lastY, setLastY] = useState<number>(0);
  const [scrollDistance, setScrollDistance] = useState<number>(0);
  const [scrollDirectionHorizontal, setScrollDirectionHorizontal] =
    useState<boolean>(false);

  // Refs
  const tabsContainerRef = useRef<HTMLDivElement | null>(null);
  const tabListScrollRef = useRef<HTMLDivElement | null>(null);
  const tabListRef = useRef<HTMLUListElement | null>(null);
  const tabsRef = useRef<HTMLLIElement[]>([]);
  const bdoWrapperRef = useRef<HTMLBodyElement | null>(null);
  // mechanism used to prevent _setActive from running continuously when scrolling the header tabs
  const controlledTabChangeCounter = useRef<number>(
    (controlledActiveTab ?? 0) + 1
  );

  // track when the tabs container width changes (instead of window resize)
  // return value used to measure if scrollability is needed (arrow navigation at certain breakpoints),
  // applying different breakpoints to the tabs container
  const { width: tabsContainerWidth } = useThrottledResizeObserver(
    tabsContainerRef as React.RefObject<HTMLElement>,
    0,
    () => {
      _setActive(tabsRef.current[renderTabs]);
    }
  );

  // track the width of the scroll container to determine the minX value
  const { width: scrollRectWidth } = useThrottledResizeObserver(
    tabListScrollRef as React.RefObject<HTMLElement>
  );

  // minX is used to determine the maximum distance the tabs can be scrolled to the
  // left (RTL) or right (LTR), (scrolling to max opposite direction is always set to 0)
  // calculate the minX value when scroll container width changes (on resize,
  // when slow-to-render nav buttons become visible and thus change the scroll container width).
  const { minX } = useDetermineMinMax(
    scrollRectWidth || 0,
    tabListRef as React.RefObject<HTMLElement>,
    (rtl = false),
    tabsContainerWidth || 0
  );

  // boolean value to determine if the tabs container is not fully expanded,
  // ie. the tabs are displayed on mobile device and/or nav buttons are visible.
  // *not associated with breakpoint, calculations based on tab visibility
  const isScrollable = useDetermineScrollability(
    {
      tabListRef: { current: tabListRef.current as HTMLElement },
      tabListScrollRef: { current: tabListScrollRef.current as HTMLElement },
    },
    tabsContainerWidth ?? 0
  );

  // return breakpoint value (xs, sm, md, lg, xl) to set on the Tabs component.
  // used to adjust width of fade element on tab container ends
  const breakpoint = useUpdateBreakPoint(breakpoints, tabsContainerWidth);

  // track previous width of the tabs container to prevent unneccessary re-renders in a useEffect
  const prevTabsWidthCounter = useRef<number>((tabsContainerWidth ?? 0) + 1);

  // used to check if tab names are unique
  const tabNames = useMemo(() => {
    return (
      Array.isArray(children) &&
      children?.map((item) => item.props.name?.toLowerCase())
    );
  }, [children]);

  /**
   * Checks and adjusts the future X coordinate based on the current direction (RTL or LTR) and resizing state.
   *
   * @param futureX - The future X coordinate to check. Defaults to the current X coordinate if undefined.
   * @returns The adjusted X coordinate based on the direction and resizing state.
   */
  const _checkX = useCallback(
    (futureX: number | undefined): number | undefined => {
      // futureX could be a zero value
      if (futureX !== undefined) {
        if (futureX < minX) {
          return minX;
        } else if (futureX > 0) {
          return 0;
        } else {
          return futureX;
        }
      }
    },
    [minX]
  );

  /**
   * Updates the position of the tab list based on the given x-coordinate.
   *
   * @param x - The x-coordinate to which the tab list should be moved. If `undefined`, the function returns `false`.
   * @returns `false` if `x` is `undefined`, otherwise `undefined`.
   */
  const updatePosition = useCallback(
    (futureX: number): number | undefined => {
      const validatedX = _checkX(futureX);
      if (tabListRef.current && validatedX !== undefined) {
        tabListRef.current.style.transform = `translateX(${
          rtl ? -validatedX : validatedX
        }px)`;
        return rtl ? -validatedX : validatedX;
      }
    },
    [rtl, _checkX]
  );

  /**
   * Moves the slider based on the provided parameters. Used across most touch, mouse, and scroll events.
   *
   * @param params - The parameters for the move operation.
   * @param params.x - The distance to move in the x direction.
   * @param params.force - If true, forces the move operation even if not dragging.
   * @param params.params - Additional parameters for the move operation.
   *
   * The function ensures that the slider stays within bounds and adds resistance
   * to the scroll if necessary. It updates the position of the slider and tracks
   * the scroll distance.
   */
  const _move = (params: {
    x?: number | undefined;
    force?: boolean;
    params?: boolean;
  }): void => {
    // maxMoveX is always zero because the slider is starts on one side, negating the need to be calculated
    // const maxMoveX = 0;
    // Make sure we're currently dragging
    if (!isDragging && !params.force) {
      return;
    }
    if (rtl && x >= -minX) return;

    const xPosition = rtl ? x - (params.x ?? 0) : x + (params.x ?? 0);
    setX((prevX): number => {
      return updatePosition(xPosition) ?? prevX;
    });
    params?.x && setScrollDistance(scrollDistance + Math.abs(params?.x));
  };

  /**
   * Handles the movement event for the tabs container on touch or mouse event.
   *
   * @param clientX - The X coordinate of the client, in pixels
   * @param clientY - The Y coordinate of the client, in pixels
   * @param e - The event object.
   *
   */
  const _onEventMove = (clientX: number, clientY: number, e: Event): void => {
    if (
      isScrollable &&
      tabsContainerRef?.current?.contains(e?.target as Node)
    ) {
      const xDistance = clientX - lastX;
      const yDistance = clientY - lastY;

      if (scrollDirectionHorizontal) {
        _move({ x: xDistance });
      } else {
        if (Math.abs(yDistance) < Math.abs(xDistance)) {
          setScrollDirectionHorizontal(true);
          _move({ x: xDistance });
        }
      }

      setLastX(clientX);
      setLastY(clientY);
    }
  };

  /**
   * When the left button is clicked, slide the tabs to the right.
   */
  const _onLeftClick = (
    event:
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
      | React.KeyboardEvent<HTMLButtonElement>
  ): void => {
    _privateProps?.arrowLeftOnClick && _privateProps?.arrowLeftOnClick(event);
    const scrollDistance = Math.round(
      (tabListScrollRef?.current?.clientWidth || 0) * 0.75
    );
    _move({
      x: rtl ? -scrollDistance : scrollDistance,
      force: true,
    });
    _stop();
  };

  /**
   * When the right button is clicked, slide the tabs to the left.
   */
  const _onRightClick = (
    event:
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
      | React.KeyboardEvent<HTMLButtonElement>
  ): void => {
    _privateProps?.arrowRightOnClick && _privateProps?.arrowRightOnClick(event);
    const scrollDistance = Math.round(
      (tabListScrollRef?.current?.clientWidth || 0) * 0.75
    );
    _move({
      x: rtl ? scrollDistance : -scrollDistance,
      force: true,
    });
    _stop();
  };

  /**
   * Stop the drag
   */
  const _stop = (): void => {
    // Stop dragging
    setIsDragging(false);
    setScrollDistance(0);
    // Reset the scroll direction
    setScrollDirectionHorizontal(false);
  };

  /**
   * When the touch is over.
   */
  const _onTouchEnd = (): void => {
    // Stop after one frame so that animation is fully reenabled
    setTimeout(() => {
      _stop();
    }, 1);
  };

  /**
   * When the mouse move is complete and the mouse is up.
   * Let go of the drag to set the scroll position where it drops
   */
  const _onMouseUp = (): void => {
    // If we haven't been dragging, get outta here!
    if (!isDragging) {
      return;
    }
    // Stop after one frame so that animation is fully reenabled
    setTimeout(() => _stop(), 1);
  };

  /**
   * Start the drag
   * @param {Object} params
   */
  const _start = (params: {
    lastX: number;
    lastY: number;
    type: string;
  }): void => {
    // Start dragging
    setIsDragging(true);
    // Stash the element and its position
    setLastX(get(params, "lastX"));
    setLastY(get(params, "lastY"));
  };

  const navClass = classNames("spark-tabs__nav", {
    scrollable: isScrollable,
    // no-animation class is used to prevent the tabs from animating when the component first renders
    "no-animation": true,
  });

  const _getValidatedActiveTab = (): number => {
    // prevents attempting to render a tab index that is invalid
    // useful when dealing with tabsets with dynamic tabs/content
    return renderTabs < React.Children.count(children) ? renderTabs : 0;
  };

  // Get the tab name prop from Tab component
  const _getTabName = (tabNumber?: number): string => {
    if (tabNumber !== undefined) {
      return (tabNames as [])?.[tabNumber];
    }
    const selectedTabNumber: number = _getValidatedActiveTab();
    return (tabNames as [])?.[selectedTabNumber] || "";
  };

  /**
   * Focus on a specific element by bringing it to the middle of the scroller.
   * @param align Which side to align with.
   */
  const _focus = useCallback(
    (
      align: "left" | "right" = "left",
      tabEl: HTMLLIElement,
      sideFadeWidth: number = 24
    ): void => {
      const scrollRect = tabListScrollRef?.current?.getBoundingClientRect();
      const tabRect = tabEl.getBoundingClientRect();

      if (!scrollRect || !tabListScrollRef.current || !tabListRef.current)
        return;

      setX((prevX): number => {
        let futureX: number;
        if (rtl) {
          futureX =
            align === "left"
              ? -(scrollRect.right - tabRect.left - prevX)
              : tabRect.right - scrollRect.left + prevX;
        } else {
          futureX =
            align === "left"
              ? prevX + (scrollRect.left - tabRect.left) + sideFadeWidth
              : prevX - (tabRect.right - scrollRect.right) - sideFadeWidth;
        }

        return updatePosition(futureX) ?? prevX;
      });
    },
    [rtl, updatePosition]
  );

  /**
   * Set the active item so that it can be scrolled into view.
   */
  const _setActive = useCallback(
    (tabEl: HTMLLIElement): void => {
      const tabRect = tabEl.getBoundingClientRect();
      // Get the bounding rectangles of the scroll container and the tab list
      const tabListScrollRect =
        tabListScrollRef?.current?.getBoundingClientRect();
      const tabUListRect = tabListRef?.current?.getBoundingClientRect();

      // If either bounding rectangle is not available, exit the function
      if (!tabListScrollRect || !tabUListRect) return;

      // Check if the tab is fully visible within the scroll container
      const isTabFullyVisible =
        tabRect.left >= tabListScrollRect.left &&
        tabListScrollRect.right >= tabRect.right;

      const isNotTabEnds =
        tabsRef.current.indexOf(tabEl) !== 0 &&
        tabsRef.current.indexOf(tabEl) !== tabsRef.current.length - 1;

      if (!isTabFullyVisible) {
        // Determine if the tab is out of view on the left or right side
        const leftOutOfView = tabRect.left < tabListScrollRect.left;
        const rightOutOfView = tabRect.right > tabListScrollRect.right;

        // because the tabs in the middle have padding on both sides,
        // its unlikely they will be positioned at the edge of the tab list,
        // so don't worry about compensating for the fade effect on the edges of the tab list
        if (leftOutOfView) {
          _focus("left", tabEl, isNotTabEnds ? 0 : undefined);
        } else if (rightOutOfView) {
          _focus("right", tabEl, isNotTabEnds ? 0 : undefined);
        }
      } else if (isScrollable) {
        // Handle the case when the tab is within the scrollable area but not fully visible
        // Ex: partially visible tabs are clickable and they will snap into full view
        // Ex: at risk of becoming out of view, keep active tab in view when resizing smaller/larger

        // sideFadeWidth is the width of the missing padding-right that all other middle
        // tabs have, but its name also denotes the fade effect on the side of the tabs (only 18px).
        // It also acts as a buffer to prevent the last active tab from snapping to the edge
        // and, therefore, partially covered by the fade effect
        const sideFadeWidth = isNotTabEnds ? 0 : 24;
        const leftOutOfView = tabRect.left < tabListScrollRect.left;
        const lastTabOutOfRightView =
          tabRect.right + sideFadeWidth > tabListScrollRect.right;
        const rightOutOfView = tabListScrollRect.right > tabUListRect.right;

        // Focus the tab into view based on its position
        if (leftOutOfView) {
          _focus("left", tabEl);
        } else if (rightOutOfView) {
          // when resizing to a larger width, allow the right side tab list to pull the entire list into view
          // don't snap active tab to the right (jolting effect)
          _focus("right", tabsRef.current[tabsRef.current.length - 1]);
        } else if (lastTabOutOfRightView) {
          // when last tab is active and resizing down,
          // smooth out the transition to the right
          _focus("right", tabEl);
        }
      }
    },
    [_focus, isScrollable]
  );

  /**
   * When the user clicks on a tab, make it active.
   */
  const _onTabListClick = (
    key: number,
    event: React.MouseEvent<HTMLLIElement>
  ): void => {
    if (controlledActiveTab !== undefined) {
      onTabClick?.(key, event);
    } else {
      // Run _setActive when new tab is clicked and its only partially visible
      _setActive(tabsRef.current[key]);
      setUnControlledActiveTab(key);
      onTabClick?.(key, event);
    }
  };

  /**
   * When the touchstart event fires, start the scrolling process
   * use case: touchstart event is fired when a finger (or touch imitation with mouse) clicks and drags the tabs header
   * @param TouchEvent touches Property
   * Return the x- and y-coordinates of the touch:
   */
  const _onTouchStart = (event: React.TouchEvent<HTMLUListElement>): void => {
    const { touches } = event;

    _start({
      lastX: touches[0].clientX,
      lastY: touches[0].clientY,
      type: "touch",
    });
  };

  /**
   * When the mouseevent fires, start the scrolling process
   * use case: mouseevent is fired when a mouse wheel or touchpad hovers to scroll the tabs header or
   * when a mouse click + drag off the header, lets go, and drags the tabs header on mobile, click anywhere to release
   * @param MouseEvent event Property
   * Return the x- and y-coordinates of the touch:
   */
  const _onMouseStart = (event: React.MouseEvent<HTMLUListElement>): void => {
    _start({
      lastX: event.clientX,
      lastY: event.clientY,
      type: "mouse",
    });
  };

  const _onScroll = (e: WheelEvent<HTMLUListElement>): void => {
    if (isScrollable) {
      // Get the value of the wheel
      // deltaX represents the distance the mouse wheel has been moved in the X-axis
      // Negative values indicate scrolling to the right, and positive values indicate scrolling to the left.
      // Multiply by 2 to make the scroll a little faster
      const val = -e.deltaX * 2;

      if (val !== undefined) {
        // Supress native
        e.preventDefault();

        _move({
          params: true,
          force: true,
          x: val,
        });
      }
    }
  };

  useEffect(() => {
    // this effect is specific in fixing responsive behavior when using the console in device specific mode
    // fixes when in desktop, clicking a tab, then resizing to mobile, the active tab header does not get centered
    // the issue is that the `minX` value continues to calculate even after the tabsContainerWidth has finished. Therefore, need to recalualte the tabs scroll again.
    _setActive(tabsRef.current[renderTabs]);
  }, [_setActive, renderTabs, minX]);

  useEffect(() => {
    if (controlledActiveTab === 0 || !!controlledActiveTab) {
      // If the controlled tab changes, update the active tab
      // using a ref to avoid the component re-rendering and running _setActive
      // continuously when scrolling the header tabs
      controlledTabChangeCounter.current !== renderTabs &&
        _setActive(tabsRef.current[renderTabs]);
    }

    // Check if tab name prop is unique in the component. If not, send a warning message to console
    const tabNamesSet = tabNames && new Set(tabNames);
    if (tabNamesSet && tabNamesSet.size !== tabNames.length) {
      console.warn("Tab name prop should contain unique values");
    }
  }, [
    _setActive,
    renderTabs,
    tabNames,
    controlledActiveTab,
    controlledTabChangeCounter,
  ]);

  useEffect(() => {
    // now that the controlled tab has been set, update the counter
    // to prevent _setActive from running continuously when scrolling on the header tabs
    if (controlledActiveTab === renderTabs) {
      controlledTabChangeCounter.current = renderTabs;
    }
    if (prevTabsWidthCounter.current !== tabsContainerWidth) {
      prevTabsWidthCounter.current = tabsContainerWidth ?? 0;
    }
  }, [controlledActiveTab, renderTabs, tabsContainerWidth]);

  useEventListener("mouseup", _onMouseUp as EventListener, window);
  useEventListener(
    "mousemove",
    (e) =>
      _onEventMove((e as MouseEvent).clientX, (e as MouseEvent).clientY, e),
    window
  );
  useEventListener(
    "touchmove",
    (e) => {
      const touch = (e as TouchEvent).touches[0];
      _onEventMove(touch.clientX, touch.clientY, e);
    },
    window
  );
  useEventListener("touchend", _onTouchEnd, window);
  useEventListener(
    "wheel",
    (e) => _onScroll(e as unknown as WheelEvent<HTMLUListElement>),
    { current: tabListRef.current as EventTarget }
  );

  const singleContent = (): ReactNode => {
    const tabsContent = React.Children.toArray(children);
    const selectedTab = _getValidatedActiveTab();
    return (
      <TabContent
        id={`tabpanel-${_getTabName()}`}
        className={classNames("spark-tabs__panel", "active")}
        aria-describedby={_getTabName()}
      >
        {tabsContent[selectedTab]}
      </TabContent>
    );
  };

  // Render all tabs content when `forceRender` is true
  const allContent = (): ReactNode => {
    return React.Children.map(children, (child, key) => {
      const isActive = isEqual(key, renderTabs);
      return (
        <TabContent
          key={key}
          id={`tabpanel-${_getTabName(key)}`}
          className={classNames("spark-tabs__panel", {
            active: isActive,
          })}
          aria-describedby={_getTabName(key)}
          aria-hidden={!isActive}
        >
          {child || ""}
        </TabContent>
      );
    });
  };

  const renderTabContent = (forceRender: boolean): ReactNode =>
    !forceRender ? singleContent() : allContent();

  const TabsContainer = (
    <div
      className={classNames("spark-tabs", breakpoint, className)}
      ref={(node: HTMLDivElement): void => {
        tabsContainerRef.current = node;
      }}
      {...rest}
    >
      <nav
        className={navClass}
        aria-label={navAriaLabel}
        aria-describedby={ariaDescribedby}
      >
        <div
          className="spark-tabs__scroll"
          ref={(node: HTMLDivElement): void => {
            tabListScrollRef.current = node;
          }}
        >
          <ul
            className="spark-tabs__list"
            ref={(node: HTMLUListElement): void => {
              tabListRef.current = node;
            }}
            role="tablist"
            aria-label={ariaLabel}
            onTouchStart={_onTouchStart}
            onMouseDown={_onMouseStart}
          >
            {React.Children.toArray(children).map(
              ({ props }: React.PropsWithChildren<any>, key: number) => {
                return (
                  <TabHeader
                    key={key}
                    ref={(node: HTMLLIElement): void => {
                      tabsRef.current[key] = node;
                    }}
                    tabName={_getTabName(key)}
                    isActive={isEqual(key, _getValidatedActiveTab())}
                    onClick={(event: React.MouseEvent<HTMLLIElement>): void => {
                      _onTabListClick(key, event);
                    }}
                    {...props}
                  />
                );
              }
            )}
          </ul>
        </div>

        <div className="spark-tabs__btns">
          <button
            type="button"
            aria-label={scrollLeftArrowLabel}
            className="spark-tabs__btn spark-icon-arrow-chevron-left spark-tabs__btn--left"
            onClick={_onLeftClick}
          />
          <button
            type="button"
            aria-label={scrollRightArrowLabel}
            className="spark-tabs__btn spark-icon-arrow-chevron-right spark-tabs__btn--right"
            onClick={_onRightClick}
          />
        </div>
      </nav>

      <div className="spark-tabs__panels">{renderTabContent(forceRender)}</div>
    </div>
  );

  const WithBDO = (
    <bdo
      dir="rtl"
      ref={(node: HTMLBodyElement): void => {
        bdoWrapperRef.current = node;
      }}
    >
      {TabsContainer}
    </bdo>
  );

  return rtl ? WithBDO : TabsContainer;
};

Tabs.displayName = "Tabs";
